import { Form } from '@epages/react-components'
import type { TFunction, TOptions } from 'i18next'
import { upperFirst } from 'lodash-es'
import { useDispatch, useSelector } from 'react-redux'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import Immutable from 'immutable'
import cc from 'classcat'
import slugify from 'speakingurl'

import PageEditPanel, { isStartpage } from './Panels/PageEditPanel'
import {
  activateEditorSidebarModule,
  createPage,
  deletePage,
  duplicatePage,
  listCategories,
  loadFooterPages,
  loadNavigation,
  updateCategorySettings,
  updateLegalSettings,
  updateNavigation,
  updatePageSettings,
} from '../../store/actions'
import { findDeep, mapDeep } from '../../utils/immutableTreeHelpers'
import { generatePageUrl, withQueryAndScope } from '../../urlGeneratorsPlain'
import { generateShopUrl } from '../../urlGenerators'
import { useTranslate, withComponents } from '../../utils/translate'
import Button from '../formComponents/Button'
import ModalDialog from '../ModalDialog'
import flatMap from '../../utils/nestedListUtils/flatMap'
import inheritVisibility from '../../utils/inheritVisibility'

const keyPrefix = 'components.editorSidebarComponent.pageManagerSection'

type PageModalRaw = {
  footerPages: ImmutableList
  mainNavigation: ImmutableList
  page: ImmutableMap | undefined
  mode: 'create' | 'edit' | 'duplicate'
  onRequestClose: () => void
  isActivePage: boolean
}

export default function PageModal({
  footerPages,
  mainNavigation,
  page,
  mode,
  onRequestClose,
  isActivePage,
}: Readonly<PageModalRaw>) {
  const t = useTranslate('interface')

  const lang = useSelector<State, string>((state) => state.getIn(['shop', 'locale'], '').substring(0, 2))
  const isBeyondShop = useSelector<State, boolean>((state) => Boolean(state.getIn(['shop', 'beyond'])))
  const mboBaseUrl = useSelector<State, string>((state) => state.getIn(['shop', 'mboBaseUrl']))
  const categories = useSelector<State, ImmutableList>((state) => state.get('categoriesList').toList())
  const dispatch = useDispatch<GlobalDispatch>()
  const location = useLocation()

  const [state, setState] = useState({
    isSubmitting: false,
    submitError: null,
    deleteRequested: false,
  })

  const navigate = useNavigate()

  const slugifyOptions = { lang, truncate: 250 }

  const pageType = page?.get('type')

  const tModal = (subKey: string, options?: TOptions | undefined) => {
    if (page) {
      const keyPartForType = {
        category: 'categories',
        blank: 'contentPages',
        legal: 'legalPages',
      }[pageType]

      return t(`${keyPrefix}.${keyPartForType}Section.pageSettingsModal.${subKey}`, options)
    }

    return t(`${keyPrefix}.${'contentPagesSection.createPageModal'}.${subKey}`, options)
  }

  const dialogTitle = tModal(pageType === 'blank' ? `title.on${upperFirst(mode)}` : 'title')

  const isBeyondHomepage = isBeyondShop && page && isStartpage(page)

  const pageSlugs = flatMap(
    mainNavigation.concat(footerPages),
    (entry) => entry.get('type') === 'blank' && entry.get('slug'),
  ).filter(Boolean)

  const handlers = {
    onDeleteRequested: () => setState((prev) => ({ ...prev, deleteRequested: true })),
    onDeleteConfirm: (page) => () => onSubmitDeletePage(page),
    onCancel: () =>
      state.deleteRequested ? setState((prev) => ({ ...prev, deleteRequested: false })) : onRequestClose(),
    onSubmit: (newPage) => {
      if (mode === 'create') onSubmitCreatePage(newPage)
      else if (mode === 'duplicate') onSubmitDuplicatePage(newPage)
      else if (mode === 'edit') onSubmitEditPage(newPage)
    },
  }

  const renderDeleteRequest = (pageTitle) => (
    <div className="ep-notification-danger">{tModal('deleteButton.warningMessage', { pageTitle })}</div>
  )

  const renderError = ({ status, message }: { status: number; message: string }) => {
    const maximumPageCountReached = message === 'Maximum page count reached.'

    return (
      <div
        className={cc({
          'ep-form-row': true,
          'ep-alert-info': status >= 500,
          'ep-notification-warning': !(status && status >= 500),
        })}
      >
        {maximumPageCountReached ? (
          <div>
            {withComponents(tModal)('errorMessages.maximumPageCountReached', {
              updateLink: (
                <a href={`${mboBaseUrl}/help`} target="_parent">
                  {tModal('errorMessages.updateLink')}
                </a>
              ),
            })}
          </div>
        ) : (
          tModal('errorMessages.generic')
        )}
      </div>
    )
  }

  const updateWholeNavigation = (newPage) => {
    // replace changed page in navigation
    const newNavigation = mapDeep(mainNavigation, (page) => (page.get('id') === newPage.get('id') ? newPage : page))

    return dispatch(
      updateNavigation(inheritVisibility(newNavigation), {
        showErrorNotification: false,
      }),
    )
  }

  const updatePage = async (newPage) => {
    const title = newPage.get('title')
    const oldPageSlug = newPage.get('slug')
    // did the slug change because of a new title?
    const newPageSlug = maybeGenerateNewSlug(oldPageSlug, title)

    const payload = {
      pageId: newPage.get('id'),
      oldPageSlug,
      newPageSlug,
      title,
      titleTag: newPage.get('titleTag'),
      metaDescription: newPage.get('metaDescription'),
      isVisible: newPage.get('isVisible'),
      navigation: newPage.get('navigation'),
    }

    const updatedPage = (await dispatch(
      updatePageSettings(payload, { showErrorNotification: false }),
    )) as unknown as Core.Page

    if (isActivePage)
      navigate(withQueryAndScope(generatePageUrl(updatedPage.type, updatedPage.slug), location), { replace: true })
  }

  const updateCategory = (newPage) => {
    const payload = {
      categoryId: newPage.get('categoryId'),
      slug: newPage.get('slug'),
      title: newPage.get('title'),
      titleTag: newPage.get('titleTag'),
      metaDescription: newPage.get('metaDescription'),
      isVisible: newPage.get('isVisible'),
      objectId: newPage.get('objectId'),
      facetedSearchCategoryStartDepth: newPage.get('facetedSearchCategoryStartDepth'),
      facetedSearchShowFacetsOnCategory: newPage.get('facetedSearchShowFacetsOnCategory'),
      visibleInNavigation: newPage.get('visibleInNavigation'),
    }

    return dispatch(updateCategorySettings(payload, { showErrorNotification: false }))
  }

  const updateLegal = (newPage) => {
    const payload = {
      slug: newPage.get('slug'),
      isVisible: newPage.get('isVisible'),
    }

    return dispatch(updateLegalSettings(payload, { showErrorNotification: false }))
  }

  const validate = (data, isOnSubmit = true) => {
    const normalizedData = normalize(data)
    const title = normalizedData.has('title') && normalizedData.get('title')
    const slug = title ? slugify(title, slugifyOptions) : ''
    const titleEmptyValidationResult = title ? null : tModal('pageNameInputField.validationMessages.isRequired')

    const slugTooShortValidationResult =
      title && slug.length < 1 ? tModal('pageNameInputField.validationMessages.onlySpecialCharacters') : null

    const navigationError =
      normalizedData.get('navigation') === 'footer' &&
      page &&
      page.get('navigation') === 'main' &&
      page.get('children') &&
      page.get('children').size > 0 &&
      tModal('pageNameInputField.validationMessages.cannotMakeFooterPageWithChildren')

    return Immutable.fromJS({
      title: isOnSubmit ? titleEmptyValidationResult || slugTooShortValidationResult || navigationError || null : null,
    })
  }

  const normalize = (data) => {
    return data.update('title', (title) => title.trim())
  }

  const maybeGenerateNewSlug = (oldPageSlug, title) => {
    return oldPageSlug !== slugify(title, slugifyOptions) && !isBeyondHomepage
      ? enforceUniqueSlug(slugify(title, slugifyOptions))
      : oldPageSlug
  }

  const enforceUniqueSlug = (slug, uniquifier = 1) => {
    if (!pageSlugs.includes(slug)) return slug

    return pageSlugs.includes(`${slug}-${uniquifier}`)
      ? enforceUniqueSlug(slug, uniquifier + 1)
      : `${slug}-${uniquifier}`
  }

  // Auto-checks the "Add pages & content" to-do in the BYD cockpit's dashboard checklist and setup guide
  const completeAddPageTask = () => {
    window.parent?.postMessage(
      {
        type: 'CHECKLIST',
        payload: {
          websiteOnly: 'contentSection.addContent',
          regularShop: 'onlineShopSetup.addPages',
        },
      },
      mboBaseUrl,
    )
  }

  const onSubmitCreatePage = async (newPage) => {
    setState((prev) => ({ ...prev, isSubmitting: true }))

    const pageParentSlug = newPage.get('pageParentSlug', null)
    const parentPage = findDeep(mainNavigation, (node) => node.get('slug') === pageParentSlug)

    const payload = {
      pageSlug: enforceUniqueSlug(slugify(newPage.get('title'), slugifyOptions)),
      title: newPage.get('title'),
      pageType: 'blank',
      navigation: newPage.get('navigation'),
      isVisible: parentPage?.get('isVisible') === false ? false : newPage.get('isVisible'),
      collectionId: newPage.getIn(['selectedCollectionInfo', 'isActive'])
        ? newPage.getIn(['selectedCollectionInfo', 'collectionId'])
        : null,
      pageParentSlug,
    } as const

    try {
      const createdPage = (await dispatch(
        createPage(payload, { showErrorNotification: false }),
      )) as unknown as Core.Page

      navigate(withQueryAndScope(generatePageUrl(createdPage.type, createdPage.slug), location))
      if (!newPage.getIn(['selectedCollectionInfo', 'isActive']))
        dispatch(activateEditorSidebarModule('content-creation'))
      if (createdPage.navigation === 'main') await dispatch(loadNavigation())
      else if (createdPage.navigation === 'footer') await dispatch(loadFooterPages())
      setState((prev) => ({ ...prev, isSubmitting: false }))
      completeAddPageTask()
      onRequestClose()
    } catch (err) {
      setState((prev) => ({ ...prev, isSubmitting: false, submitError: err }))
    }
  }

  const onSubmitDuplicatePage = async (newPage) => {
    setState((prev) => ({ ...prev, isSubmitting: true }))

    const slug = page?.get('slug')
    const payload = {
      newSlug: enforceUniqueSlug(slugify(newPage.get('title'), slugifyOptions)),
      newTitle: newPage.get('title'),
    }

    try {
      const duplicatedPage = (await dispatch(
        duplicatePage(slug, payload, { showErrorNotification: false }),
      )) as unknown as Core.Page

      navigate(withQueryAndScope(generatePageUrl(duplicatedPage.type, duplicatedPage.slug), location))
      if (duplicatedPage.navigation === 'main') await dispatch(loadNavigation())
      else if (duplicatedPage.navigation === 'footer') await dispatch(loadFooterPages())
      setState((prev) => ({ ...prev, isSubmitting: false }))
      completeAddPageTask()
      onRequestClose()
    } catch (err) {
      setState((prev) => ({ ...prev, isSubmitting: false, submitError: err }))
    }
  }

  const onSubmitEditPage = async (newPage) => {
    const type = newPage.get('type')
    const shouldHideChildren = newPage.get('children').count() > 0 && !newPage.get('isVisible')

    setState((prev) => ({ ...prev, isSubmitting: true }))

    const runUpdate = (() => {
      switch (type) {
        case 'blank':
          return shouldHideChildren ? updateWholeNavigation(newPage) : updatePage(newPage)
        case 'category':
          return updateCategory(newPage)
        case 'legal':
          return updateLegal(newPage)
        default:
          return Promise.resolve()
      }
    })()

    try {
      await runUpdate

      setState((prev) => ({ ...prev, isSubmitting: false }))
      completeAddPageTask()
      onRequestClose()
    } catch (submitError) {
      setState((prev) => ({ ...prev, isSubmitting: false, submitError }))
    }

    return null
  }

  const onSubmitDeletePage = async (page) => {
    await dispatch(deletePage(page.get('slug')))

    if (isActivePage) navigate(withQueryAndScope(generateShopUrl(), location))

    onRequestClose()

    return null
  }

  // Only show the delete button for blank pages and not for categories, legal pages or the homepage in Beyond shops
  const showDeleteButton = mode === 'edit' && page && page.get('type') === 'blank' && !isBeyondHomepage

  useEffect(() => {
    if (isBeyondShop && !categories.size) {
      dispatch(listCategories())
    }
  }, [dispatch, isBeyondShop, categories.size])

  return (
    <ModalDialog isOpen contentLabel={dialogTitle} onRequestClose={onRequestClose}>
      <Form
        name="addPage"
        value={
          mode === 'duplicate'
            ? page?.set(
                'title',
                `${page.get('title')}-${t(`${keyPrefix}.contentPagesSection.duplicatePageModal.copy`)}`,
              )
            : page
        }
        onSubmit={handlers.onSubmit}
        validate={validate}
        normalize={normalize}
      >
        {({ value }) => (
          <>
            <ModalDialog.Header title={dialogTitle} />
            <ModalDialog.Body>
              {state.submitError && renderError(state.submitError)}
              {state.deleteRequested ? (
                renderDeleteRequest(value.get('title'))
              ) : (
                <PageEditPanel
                  changedPage={value}
                  page={page || Immutable.Map()}
                  tModal={tModal as TFunction}
                  maybeGenerateNewSlug={maybeGenerateNewSlug}
                  isBeyondShop={isBeyondShop}
                  categories={categories}
                  mode={mode}
                  mainNavigation={mainNavigation}
                />
              )}
            </ModalDialog.Body>
            <ModalDialog.Footer>
              {state.deleteRequested && (
                <>
                  <Button
                    type="button"
                    variant="cancel"
                    disabled={state.isSubmitting}
                    onClick={() => handlers.onCancel()}
                  >
                    {tModal('cancelButton.label')}
                  </Button>

                  <Button
                    type="button"
                    variant="danger"
                    disabled={state.isSubmitting}
                    onClick={handlers.onDeleteConfirm(value)}
                  >
                    {tModal('deleteConfirmButton.label')}
                  </Button>
                </>
              )}

              {!state.deleteRequested && showDeleteButton && (
                <Button
                  type="button"
                  variant="danger"
                  disabled={state.isSubmitting}
                  onClick={() => handlers.onDeleteRequested()}
                >
                  {tModal('deleteButton.label')}
                </Button>
              )}

              {!state.deleteRequested && (
                <>
                  <Button
                    type="button"
                    variant="cancel"
                    disabled={state.isSubmitting}
                    onClick={() => handlers.onCancel()}
                  >
                    {tModal('cancelButton.label')}
                  </Button>

                  <Button type="submit" disabled={state.isSubmitting}>
                    {tModal('saveButton.label')}
                  </Button>
                </>
              )}
            </ModalDialog.Footer>
          </>
        )}
      </Form>
    </ModalDialog>
  )
}
